/**
 * AI Chatbot Mixpanel event constants.
 * This file mirrors iOS app events (with first letter capitalized) and backend events (if applicable).
 */

import { Dict } from 'mixpanel-browser';
import { FeedbackCategory } from '../react/applications/askmedi/api/chatSessions';

/**
 * Maps to iOS AIChatbotAssistantType enum.
 */
export const AIChatbotAssistantType = {
    ASKMEDI: 'askmedi',
    GENERAL: 'general_assistant',
    VIDEO: 'video_assistant',
} as const;

export type AIChatbotAssistantTypeValue = (typeof AIChatbotAssistantType)[keyof typeof AIChatbotAssistantType];

/**
 * Maps to iOS AIChatbotQuestionSource enum.
 */
export const AIChatbotQuestionSource = {
    PREDEFINED_QUESTION: 'predefined_question',
    CUSTOM_QUESTION: 'custom_question',
    NEXT_BEST_QUESTION: 'next_best_question',
    DYNAMIC_QUESTION: 'dynamic_question',
} as const;

export type AIChatbotQuestionSourceValue = (typeof AIChatbotQuestionSource)[keyof typeof AIChatbotQuestionSource];

/**
 * Last-touch origin of an Askmedi session, distinct from `AIChatbotQuestionSource`
 * (the type of a question) and reported once on `AiChatbotOpened`. The homepage
 * hero splits into custom (typed) vs predefined (prompt) here so both are
 * distinguishable on that event without question_source.
 */
export const AIChatbotEntryPoint = {
    HOMEPAGE_CUSTOM: 'homepage_custom',
    HOMEPAGE_PREDEFINED: 'homepage_predefined',
    NAV_BAR: 'nav_bar',
    HIGHLIGHTED_CONTENT_BAR: 'highlighted_content_bar',
    EMAIL: 'email',
    SEARCH: 'search',
    SHARED_RESPONSE: 'shared_response',
    DIRECT: 'direct',
} as const;

export type AIChatbotEntryPointValue = (typeof AIChatbotEntryPoint)[keyof typeof AIChatbotEntryPoint];

/**
 * Trigger type for expand/collapse events.
 * Distinguishes between automatic (system-initiated) and manual (user-initiated) actions.
 */
export const AIChatbotCollapseTrigger = {
    AUTO: 'auto',
    MANUAL: 'manual',
} as const;

export type AIChatbotCollapseTriggerValue = (typeof AIChatbotCollapseTrigger)[keyof typeof AIChatbotCollapseTrigger];

/**
 * Event names for AI Chatbot Mixpanel events.
 * Names match iOS app events (with first letter capitalized).
 */
export const AIChatbotEvents = {
    LOADED: 'AiChatbotLoaded',
    OPENED: 'AiChatbotOpened',
    EXPANDED: 'AiChatbotExpanded',
    COLLAPSED: 'AiChatbotCollapsed',
    /**
     * @deprecated
     *  */
    URL_SELECTED: 'AiChatbotURLSelected',
    LINK_CLICKED: 'AiChatbotLinkClicked',
    LINK_PREVIEW_POPUP_OPENED: 'AiChatbotLinkPreviewPopupOpened',
    PROFILE_PREVIEW_POPUP_OPENED: 'AiChatbotProfilePreviewPopupOpened',
    PROFILE_PANEL_OPENED: 'AiChatbotProfilePanelOpened',
    VIDEO_PREVIEW_POPUP_OPENED: 'AiChatbotVideoPreviewPopupOpened',
    VIDEO_PANEL_OPENED: 'AiChatbotVideoPanelOpened',
    VIDEO_OPENED: 'AiChatbotVideoOpened',
    VIDEO_SEEKED: 'AiChatbotVideoSeeked',
    SIDEBAR_OPENED: 'AiChatbotSidebarOpened',
    NEW_QUESTION_CLICKED: 'AiChatbotNewQuestionClicked',
    CONVERSATION_OPENED: 'AiChatbotConversationOpened',
    FEEDBACK_WINDOW_OPENED: 'AiChatbotFeedbackWindowOpened',
    FEEDBACK_SUBMITTED: 'AiChatbotFeedbackSubmitted',
    QUESTION_SUBMITTED: 'AiChatbotQuestionSubmitted',
    STARTER_SHOWN: 'AiChatbotStarterShown',
    RESPONSE_STREAM_STARTED: 'AiChatbotResponseStreamStarted',
    RESPONSE_STREAM_FINISHED: 'AiChatbotResponseStreamFinished',
    RESPONSE_STREAM_FAILED: 'AiChatbotResponseStreamFailed',

    // Tooltips
    INFO_MODAL_APPEAR: 'AiChatbotVideoInfoModalViewAppear',
    INFO_MODAL_CLICKED: 'AiChatbotVideoInfoModalButtonSelected',
    TOOLTIP_APPEAR: 'AiChatbotVideoTooltipViewAppear',
    TOOLTIP_CLICKED: 'AiChatbotVideoTooltipButtonSelected',

    // In-Video Prompts
    IN_VIDEO_PROMPT_SHOWN: 'AiChatbotInVideoPromptShown',
    IN_VIDEO_PROMPT_CLICKED: 'AiChatbotInVideoPromptClicked',
    IN_VIDEO_PROMPT_DISMISSED: 'AiChatbotInVideoPromptDismissed',
    IN_VIDEO_PROMPT_TIMEOUT: 'AiChatbotInVideoPromptTimeout',
    IN_VIDEO_PROMPT_BLOCKED: 'AiChatbotInVideoPromptBlocked',
} as const;

export type AIChatbotEventName = (typeof AIChatbotEvents)[keyof typeof AIChatbotEvents];

/**
 * Per-event required properties. Events not listed here accept any Dict.
 */
interface AIChatbotEventPropertiesMap {
    [AIChatbotEvents.LOADED]: { assistant_type: AIChatbotAssistantTypeValue };
    // entry_point is optional — only the Askmedi app sets it.
    [AIChatbotEvents.OPENED]: { assistant_type: AIChatbotAssistantTypeValue; entry_point?: AIChatbotEntryPointValue };
    [AIChatbotEvents.SIDEBAR_OPENED]: { assistant_type: AIChatbotAssistantTypeValue };
    [AIChatbotEvents.CONVERSATION_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        surface: 'sidebar';
        position: number;
    };
    [AIChatbotEvents.NEW_QUESTION_CLICKED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        element: 'new_question_button';
        surface: 'sidebar';
    };
    [AIChatbotEvents.FEEDBACK_WINDOW_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        element: 'helpful_button' | 'not_helpful_button';
    };
    [AIChatbotEvents.FEEDBACK_SUBMITTED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        feedback_type: 'helpful' | 'not_helpful';
        reason?: FeedbackCategory;
        additional_feedback_populated: boolean;
    };
    [AIChatbotEvents.QUESTION_SUBMITTED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        question_source: AIChatbotQuestionSourceValue;
        question: string;
        chat_session_uuid: string;
        message_index: number;
    };
    [AIChatbotEvents.STARTER_SHOWN]: {
        assistant_type: AIChatbotAssistantTypeValue;
        question_source: string;
        question: string;
        question_id: number;
        position: number;
    };
    [AIChatbotEvents.RESPONSE_STREAM_STARTED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
    };
    [AIChatbotEvents.RESPONSE_STREAM_FINISHED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
    };
    [AIChatbotEvents.RESPONSE_STREAM_FAILED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
    };
    [AIChatbotEvents.LINK_CLICKED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        url: string;
        element: 'inline_pill' | 'preview_view_source_button' | 'external_source_link';
        position: number;
    };
    [AIChatbotEvents.LINK_PREVIEW_POPUP_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        element: 'inline_pill';
    };
    [AIChatbotEvents.PROFILE_PANEL_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        profile_uuid: string;
        element: 'inline_link' | 'headline' | 'preview_profile_name' | 'preview_more_about_button';
    };
    [AIChatbotEvents.PROFILE_PREVIEW_POPUP_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        profile_uuid: string;
        element: 'inline_link' | 'headline';
        position: number;
    };
    [AIChatbotEvents.VIDEO_PREVIEW_POPUP_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        video_timestamp_seconds: number;
        video_slug: string;
        element: 'inline_pill' | 'referenced_videos';
        position: number;
    };
    [AIChatbotEvents.VIDEO_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        video_timestamp_seconds: number;
        video_slug: string;
        element: 'video_panel';
    };
    [AIChatbotEvents.VIDEO_PANEL_OPENED]: {
        assistant_type: AIChatbotAssistantTypeValue;
        chat_session_uuid: string;
        message_index: number;
        video_timestamp_seconds: number;
        video_slug: string;
        element:
            | 'inline_pill'
            | 'referenced_videos'
            | 'preview_watch_button'
            | 'preview_thumbnail'
            | 'preview_video_title';
        position: number;
    };
}

/** Resolve the properties type for a given event name. */
type EventProperties<E extends AIChatbotEventName> = E extends keyof AIChatbotEventPropertiesMap
    ? AIChatbotEventPropertiesMap[E] & Dict
    : Dict;

/**
 * Get assistant type based on whether we're on a video page.
 */
export const getAssistantType = (videoSlug: string | null): AIChatbotAssistantTypeValue => {
    return videoSlug ? AIChatbotAssistantType.VIDEO : AIChatbotAssistantType.GENERAL;
};

/**
 * Track an AI Chatbot event via Mixpanel.
 * Events listed in AIChatbotEventPropertiesMap enforce required properties at compile time.
 */
export const trackAIChatbotEvent = <E extends AIChatbotEventName>(eventName: E, properties: EventProperties<E>) => {
    window.vumedi?.mixpanel?.track(eventName, properties);
};
